import { useState, useEffect, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { db, storage } from "./firebase";

import {
  doc,
  getDoc,
  setDoc
} from "./access/firestore";

import { useClinic }
  from "./context/ClinicContext";

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";

export default function FotosPanel({ pacienteId }) {

  const { clinicId } =
  useClinic();

  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(true);

  const [busqueda, setBusqueda] = useState("");

  const [editorImg, setEditorImg] = useState(null);

  const [slotActivo, setSlotActivo] = useState("");
  const [sesionActiva, setSesionActiva] = useState(null);

  const [subiendo, setSubiendo] = useState(false);

  const [preview, setPreview] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const [undoData, setUndoData] = useState(null);

  const [progress, setProgress] = useState(100);

  const undoTimeout = useRef(null);
  const progressInterval = useRef(null);

  const [crop, setCrop] = useState({
    unit: "%",
    x: 10,
    y: 10,
    width: 80,
    height: 80,
  });

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const imagenRecorteRef = useRef(null);

  const [zoom, setZoom] = useState(1);

  const [rotation, setRotation] = useState(0);

  const [flipX, setFlipX] = useState(false);

  const [brillo, setBrillo] = useState(100);
  const [contraste, setContraste] = useState(100);
  const [nitidez, setNitidez] = useState(0);
  const [imagenAspectRatio, setImagenAspectRatio] = useState(4 / 3);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [modoRecorte, setModoRecorte] = useState(false);
  const [proporcionRecorte, setProporcionRecorte] = useState("libre");

  useEffect(() => {
    if (!editorImg) return;

    const imagen = new Image();

    imagen.onload = () => {
      if (imagen.naturalWidth && imagen.naturalHeight) {
        setImagenAspectRatio(
          imagen.naturalWidth / imagen.naturalHeight
        );
      }
    };

    imagen.src = editorImg;
  }, [editorImg]);

  const fileInputRef = useRef();

  const pacienteRef =
  clinicId && pacienteId
    ? doc(
        db,
        "clinics",
        clinicId,
        "pacientes",
        pacienteId
      )
    : null;

  // =========================
  // LOAD
  // =========================

  useEffect(() => {

    if (!pacienteId || !clinicId) {

      setSesiones([]);

      setLoading(false);

      return;
    }


    let cancelado = false;


    const cargar = async () => {

      setLoading(true);


      try {

        const referencia = doc(
          db,
          "clinics",
          clinicId,
          "pacientes",
          pacienteId
        );


        const snap =
          await getDoc(
            referencia
          );


        if (
          cancelado
        ) {
          return;
        }


        if (
          !snap.exists()
        ) {

          setSesiones([]);

          return;
        }


        const data =
          snap.data();


        const sesionesFirestore =
          Array.isArray(
            data?.sesiones
          )
            ? data.sesiones
            : [];


        const normalizadas =
          sesionesFirestore.map(
            (s, i) => {

              const numeroValido =
                typeof s?.numero ===
                  "number" &&
                !isNaN(
                  s.numero
                );


              return {

                ...s,

                numero:
                  numeroValido
                    ? s.numero
                    : (
                        sesionesFirestore.length -
                        i
                      ),

                fotos:
                  s?.fotos &&
                  typeof s.fotos ===
                    "object"
                    ? s.fotos
                    : {}

              };

            }
          );


        setSesiones(
          normalizadas
        );

      } catch (error) {

        if (!cancelado) {

          console.error(
            "Error cargando sesiones:",
            error
          );

          setSesiones([]);

        }

      } finally {

        if (!cancelado) {

          setLoading(false);

        }

      }

    };


    cargar();


    return () => {

      cancelado = true;

    };

  }, [
    pacienteId,
    clinicId
  ]);

  // =========================
  // ESC
  // =========================

  useEffect(() => {

    const esc = (e) => {

      if (e.key === "Escape") {

        setPreview(null);

        setEditorImg(null);

        setConfirmDelete(null);
      }
    };

    window.addEventListener("keydown", esc);

    return () => {

      window.removeEventListener(
        "keydown",
        esc
      );
    };

  }, []);

  useEffect(() => {

    return () => {

      clearTimeout(
        undoTimeout.current
      );

      clearInterval(
        progressInterval.current
      );

    };

  }, []);

  // =========================
  // CURSOR
  // =========================

  useEffect(() => {

    if (editorImg) {
      document.body.style.cursor = "default";
    }

    return () => {
      document.body.style.cursor = "auto";
    };

  }, [editorImg]);

  // =========================
  // HELPERS
  // =========================

  const formatearFecha = () => {

    const d = new Date();

    return d.toLocaleDateString("es-MX") + ", " +
      d.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit"
      });
  };

  const sesionesFiltradas = sesiones.filter(s => {

    const numero = s.numero ?? "";

    const fecha = s.fecha ?? "";

    return (
      numero.toString().includes(busqueda) ||
      fecha.toLowerCase().includes(
        busqueda.toLowerCase()
      )
    );
  });

  // =========================
  // NUEVA SESIÓN
  // =========================

  const nuevaSesion = async () => {

    const numero =
      sesiones.length > 0
        ? Math.max(
            ...sesiones.map(
              s => s.numero || 0
            )
          ) + 1
        : 1;

    const nueva = {
      numero,
      fecha: formatearFecha(),
      fotos: {}
    };

    const updated = [nueva, ...sesiones];

    setSesiones(updated);

    await setDoc(
      pacienteRef,
      {
        sesiones: updated
      },
      { merge: true }
    );
  };
  // Fix de normalización de imagen

  const normalizeImage = (src) => {

  return new Promise((resolve) => {

    const img = new Image();

    img.onload = () => {

      const canvas =
        document.createElement("canvas");

      const ctx =
        canvas.getContext("2d");

      canvas.width =
        img.naturalWidth;

      canvas.height =
        img.naturalHeight;

      ctx.drawImage(img, 0, 0);

      resolve(
        canvas.toDataURL(
          "image/jpeg",
          1
        )
      );
    };

    img.src = src;
  });
};

  // =========================
  // SELECCIONAR
  // =========================

const seleccionar = async (file) => {

  setCrop({ x: 0, y: 0 });

  setZoom(1);

  setRotation(0);

  setFlipX(false);

  if (typeof file === "string") {

    const normalized =
      await normalizeImage(file);

    setEditorImg(normalized);

    return;
  }

  const reader = new FileReader();

  reader.onload = async (e) => {

    const normalized =
      await normalizeImage(
        e.target.result
      );

    setEditorImg(normalized);
  };

  reader.readAsDataURL(file);

    if (fileInputRef.current) {

      fileInputRef.current.value =
        "";
    }
};

  // =========================
  // CROP REAL
  // =========================

  const getCroppedImg = async () => {
    const image = imagenRecorteRef.current;

    if (!image || !croppedAreaPixels || !editorImg) {
      throw new Error(
        "Selecciona un área de recorte antes de guardar."
      );
    }

    if (
      !image.complete ||
      !image.naturalWidth ||
      !image.naturalHeight ||
      !image.width ||
      !image.height
    ) {
      throw new Error(
        "La imagen todavía no está lista para recortarse."
      );
    }

    const {
      x,
      y,
      width,
      height,
    } = croppedAreaPixels;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const sourceX = Math.max(
      0,
      Math.round(x * scaleX)
    );

    const sourceY = Math.max(
      0,
      Math.round(y * scaleY)
    );

    const sourceWidth = Math.min(
      image.naturalWidth - sourceX,
      Math.round(width * scaleX)
    );

    const sourceHeight = Math.min(
      image.naturalHeight - sourceY,
      Math.round(height * scaleY)
    );

    if (sourceWidth <= 0 || sourceHeight <= 0) {
      throw new Error(
        "El área seleccionada no tiene dimensiones válidas."
      );
    }

    const canvas = document.createElement("canvas");

    canvas.width = sourceWidth;
    canvas.height = sourceHeight;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error(
        "No se pudo crear el lienzo de recorte."
      );
    }

    ctx.filter = `
      brightness(${brillo}%)
      contrast(${contraste}%)
    `;

    ctx.translate(
      flipX ? canvas.width : 0,
      0
    );

    ctx.scale(
      flipX ? -1 : 1,
      1
    );

    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.filter = "none";

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(
              new Error(
                "No se pudo generar la fotografía recortada."
              )
            );
            return;
          }

          resolve(
            new File(
              [blob],
              "fotografia-clinica.jpg",
              { type: "image/jpeg" }
            )
          );
        },
        "image/jpeg",
        0.98
      );
    });
  };

  // =========================
  // GUARDAR
  // =========================

  const guardar = async () => {
    if (!clinicId) {
      alert(
        "No se encontró la clínica"
      );
      return;
    }
    if (
      !slotActivo ||
      !sesionActiva ||
      !croppedAreaPixels
    ) return;

  setSubiendo(true);

  try {

    const file =
      await getCroppedImg();

    if (!file) return;

    const path =
      `clinics/${clinicId}/pacientes/${pacienteId}/fotos/${Date.now()}.jpg`;

    const storageRef =
      ref(storage, path);

    await uploadBytes(
      storageRef,
      file
    );

    const url =
      await getDownloadURL(
        storageRef
      );

    const nuevas =
      sesiones.map(s => {

        if (
          s.numero ===
            sesionActiva.numero &&
          s.fecha ===
            sesionActiva.fecha
        ) {

          return {

            ...s,

            fotos: {

              ...s.fotos,

              [slotActivo]: {
                url,
                path
              }

            }

          };
        }

        return s;
      });

    setSesiones(nuevas);

    await setDoc(
      pacienteRef,
      {
        sesiones: nuevas
      },
      { merge: true }
    );

    setEditorImg(null);

  } catch(err) {

    console.error(
      "Error guardando foto:",
      err
    );

  } finally {

    setSubiendo(false);

  }
};

  // =========================
  // ELIMINAR
  // =========================

  const eliminarFoto = (
    sesion,
    nombre
  ) => {

    setConfirmDelete({
      tipo: "foto",
      sesion,
      nombre
    });
  };

  const eliminarSesion = (
    sesion
  ) => {

    setConfirmDelete({
      tipo: "sesion",
      sesion
    });
  };

  // =========================
  // CONFIRMAR FOTO
  // =========================

  const confirmarEliminarFoto = async (
    sesion,
    nombre
  ) => {

    const foto =
      sesion.fotos?.[nombre];

    if (!foto) return;

    const nuevas = sesiones.map(s => {

      if (
        s.numero === sesion.numero &&
        s.fecha === sesion.fecha
      ) {

        const nuevasFotos = {
          ...s.fotos
        };

        delete nuevasFotos[nombre];

        return {
          ...s,
          fotos: nuevasFotos
        };
      }

      return s;
    });

    setSesiones(nuevas);

    setUndoData({
      tipo: "foto",
      sesionesOriginales: sesiones
    });

    iniciarUndo(async () => {

      if (foto.path) {

        try {

          await deleteObject(
            ref(storage, foto.path)
          );

        } catch {}
      }

      await setDoc(
        pacienteRef,
        {
          sesiones: nuevas
        },
        { merge: true }
      );
    });

    setConfirmDelete(null);
  };

  // =========================
  // CONFIRMAR SESIÓN
  // =========================

  const confirmarEliminarSesion =
    async (sesion) => {

      const nuevas =
        sesiones.filter(s => {

          return !(
            s.numero ===
              sesion.numero &&
            s.fecha ===
              sesion.fecha
          );
        });

      setSesiones(nuevas);

      setUndoData({
        tipo: "sesion",
        sesionesOriginales: sesiones
      });

      iniciarUndo(async () => {

        const fotos =
          Object.values(
            sesion.fotos || {}
          );

        for (const f of fotos) {

          if (f?.path) {

            try {

              await deleteObject(
                ref(storage, f.path)
              );

            } catch {}
          }
        }

        await setDoc(
          pacienteRef,
          {
            sesiones: nuevas
          },
          { merge: true }
        );
      });

      setConfirmDelete(null);
    };

  // =========================
  // UNDO
  // =========================

  const iniciarUndo = (
    callbackFinal
  ) => {

    clearTimeout(
      undoTimeout.current
    );

    clearInterval(
      progressInterval.current
    );

    setProgress(100);

    progressInterval.current =
      setInterval(() => {

        setProgress(prev =>
          Math.max(prev - 2.5, 0)
        );

      }, 100);

    undoTimeout.current =
      setTimeout(async () => {

        clearInterval(
          progressInterval.current
        );

        await callbackFinal();

        setUndoData(null);

      }, 4000);
  };

  const deshacer = () => {

    clearTimeout(
      undoTimeout.current
    );

    clearInterval(
      progressInterval.current
    );

    setSesiones(
      undoData.sesionesOriginales
    );

    setUndoData(null);
  };

  if (!pacienteId) return null;

  if (loading) {
    return <div>Cargando...</div>;
  }

    return (
    <div
      style={{
        width: "100%",
        maxWidth: 1200,
        minWidth: 0,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: 22,
        color: "#142746",
        fontFamily: "Inter, system-ui, Arial",
        boxSizing: "border-box",
      }}
    >
      {/* ENCABEZADO */}
      <section
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 18,
          padding: "22px 24px",
          border: "1px solid #dce6f4",
          borderRadius: 18,
          background: "linear-gradient(135deg, #ffffff 0%, #f3f7ff 100%)",
          boxShadow: "0 5px 18px rgba(25, 55, 100, 0.05)",
          boxSizing: "border-box",
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 240px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 7,
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                borderRadius: 12,
                background: "#e8f0ff",
                color: "#2458b3",
                fontSize: 23,
              }}
            >
              ▧
            </div>

            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 21,
                  fontWeight: 800,
                  letterSpacing: "-0.4px",
                }}
              >
                Fotografías clínicas
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  color: "#71819a",
                  fontSize: 12,
                }}
              >
                Registro fotográfico y seguimiento visual del paciente
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 13,
            }}
          >
            <span
              style={{
                padding: "6px 10px",
                borderRadius: 20,
                background: "#eaf2ff",
                color: "#2458b3",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {sesiones.length} {sesiones.length === 1 ? "sesión" : "sesiones"}
            </span>

            <span
              style={{
                padding: "6px 10px",
                borderRadius: 20,
                background: "#f1f5f9",
                color: "#52627b",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {sesiones.reduce(
                (total, s) =>
                  total +
                  Object.values(s.fotos || {}).filter(
                    foto => Boolean(foto?.url || foto)
                  ).length,
                0
              )}{" "}
              fotografías registradas
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative" }}>
            <input
              placeholder="Buscar sesión..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              aria-label="Buscar sesión"
              style={{
                width: "min(240px, 65vw)",
                minHeight: 43,
                padding: "10px 13px 10px 37px",
                borderRadius: 11,
                border: "1px solid #d9e3f1",
                outline: "none",
                background: "#fff",
                color: "#142746",
                fontSize: 12,
                boxSizing: "border-box",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 13,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#7b8ca5",
                pointerEvents: "none",
              }}
            >
              ⌕
            </span>
          </div>

          <button
            type="button"
            onClick={nuevaSesion}
            style={{
              minHeight: 43,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "10px 17px",
              borderRadius: 11,
              border: "1px solid #2458b3",
              background: "linear-gradient(135deg, #3268d7, #2453a5)",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 750,
              fontSize: 12,
              boxShadow: "0 5px 12px rgba(36, 88, 179, .18)",
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            Nueva sesión
          </button>
        </div>
      </section>

      {/* LISTA DE SESIONES */}
      {sesionesFiltradas.length === 0 ? (
        <div
          style={{
            padding: "48px 20px",
            textAlign: "center",
            background: "#fff",
            border: "1px dashed #cbd8e9",
            borderRadius: 18,
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>▧</div>
          <h3 style={{ margin: "0 0 7px", fontSize: 17 }}>
            {busqueda ? "No encontramos sesiones" : "Aún no hay fotografías clínicas"}
          </h3>
          <p
            style={{
              margin: "0 auto 18px",
              maxWidth: 390,
              color: "#71819a",
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            {busqueda
              ? "Prueba con otro número de sesión o fecha."
              : "Crea una sesión para comenzar a organizar las fotografías clínicas del paciente."}
          </p>

          {!busqueda && (
            <button
              type="button"
              onClick={nuevaSesion}
              style={{
                padding: "11px 17px",
                border: "none",
                borderRadius: 10,
                background: "#2458b3",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Crear primera sesión
            </button>
          )}
        </div>
      ) : (
        sesionesFiltradas.map(s => {
          const nombresFotos = [
            "Frontal",
            "Perfil",
            "Sonrisa",
            "Oclusal superior",
            "Oclusal inferior",
            "Lateral derecha",
            "Frontal intraoral",
            "Lateral izquierda",
          ];

          const fotosCompletas = nombresFotos.filter(nombre => {
            const foto = s.fotos?.[nombre];
            return Boolean(foto?.url || foto);
          }).length;

          return (
            <section
              key={`${s.numero}-${s.fecha}`}
              style={{
                minWidth: 0,
                overflow: "hidden",
                background: "#fff",
                border: "1px solid #dce6f4",
                borderRadius: 18,
                boxShadow: "0 5px 18px rgba(25, 55, 100, 0.045)",
              }}
            >
              {/* CABECERA DE SESIÓN */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 14,
                  padding: "17px 20px",
                  borderBottom: "1px solid #e8eef7",
                  background: "linear-gradient(135deg, #f8fbff, #fff)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: 43,
                      height: 43,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 12,
                      background: "#eaf2ff",
                      color: "#2458b3",
                      fontSize: 19,
                      fontWeight: 800,
                    }}
                  >
                    {s.numero}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 800,
                        color: "#172b4d",
                      }}
                    >
                      Sesión #{s.numero}
                    </h3>
                    <div
                      style={{
                        marginTop: 5,
                        color: "#71819a",
                        fontSize: 11,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {s.fecha || "Fecha no registrada"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      padding: "7px 10px",
                      borderRadius: 20,
                      background: fotosCompletas === nombresFotos.length
                        ? "#eaf9ef"
                        : "#f1f5f9",
                      color: fotosCompletas === nombresFotos.length
                        ? "#16834a"
                        : "#52627b",
                      fontSize: 11,
                      fontWeight: 750,
                    }}
                  >
                    {fotosCompletas}/{nombresFotos.length} fotos
                  </span>

                  <button
                    type="button"
                    title="Eliminar sesión"
                    aria-label={`Eliminar sesión ${s.numero}`}
                    onClick={() => eliminarSesion(s)}
                    style={{
                      width: 34,
                      height: 34,
                      display: "grid",
                      placeItems: "center",
                      border: "1px solid #fee2e2",
                      borderRadius: 10,
                      background: "#fff7f7",
                      color: "#dc2626",
                      cursor: "pointer",
                      fontSize: 17,
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* GALERÍA */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 205px), 1fr))",
                  gap: 15,
                  padding: 20,
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              >
                {nombresFotos.map(nombre => (
                  <Slot
                    key={nombre}
                    nombre={nombre}
                    s={s}
                    {...{
                      setSlotActivo,
                      setSesionActiva,
                      fileInputRef,
                      seleccionar,
                      eliminarFoto,
                      setPreview,
                    }}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {/* INPUT */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={e => {

          const file =
            e.target.files?.[0];

          if (!file) return;

          seleccionar(file);

        }}
        style={{
          display: "none"
        }}
      />

      {/* PREVIEW */}
      {preview && (

        <div
          onClick={() =>
            setPreview(null)
          }
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(0,0,0,0.96)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999
          }}
        >

          <img
            src={preview}
            alt="preview"
            style={{
              maxWidth: "94vw",
              maxHeight: "94vh",
              borderRadius: 16
            }}
          />

        </div>

      )}

      {/* CONFIRM DELETE */}
      {confirmDelete && (

        <div style={{
          position: "fixed",
          inset: 0,
          background:
            "rgba(0,0,0,0.45)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999999
        }}>

          <div style={{
            width: 340,
            background: "#fff",
            borderRadius: 18,
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 18
          }}>

            <div style={{
              fontSize: 18,
              fontWeight: 700
            }}>
              Confirmar eliminación
            </div>

            <div style={{
              color: "#4b5563",
              lineHeight: 1.5
            }}>
              {confirmDelete.tipo === "foto"
                ? "¿Eliminar esta fotografía?"
                : "¿Eliminar esta sesión completa?"}
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10
            }}>

              <button
                onClick={() =>
                  setConfirmDelete(
                    null
                  )
                }
                style={{
                  background: "#e5e7eb",
                  border: "none",
                  padding:
                    "10px 16px",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                Cancelar
              </button>

              <button
                onClick={() => {

                  if (
                    confirmDelete.tipo ===
                    "foto"
                  ) {

                    confirmarEliminarFoto(
                      confirmDelete.sesion,
                      confirmDelete.nombre
                    );

                  } else {

                    confirmarEliminarSesion(
                      confirmDelete.sesion
                    );
                  }
                }}
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  padding:
                    "10px 16px",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontWeight: 700
                }}
              >
                Eliminar
              </button>

            </div>

          </div>

        </div>

      )}

      {/* UNDO */}
      {undoData && (

        <div style={{
          position: "fixed",
          bottom: "calc(110px + env(safe-area-inset-bottom, 0px))",
          left: "50%",
          transform:
            "translateX(-50%)",
          background: "#111827",
          color: "#fff",
          padding: "16px 20px",
          borderRadius: 16,
          display: "flex",
          alignItems: "center",
          gap: 14,
          overflow: "hidden",
          zIndex: 999999
        }}>

          <div>
            {undoData.tipo === "foto"
              ? "Foto eliminada"
              : "Sesión eliminada"}
          </div>

          <button
            onClick={deshacer}
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600
            }}
          >
            Deshacer
          </button>

          <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 4,
            width: `${progress}%`,
            background: "#3b82f6",
            transition:
              "width 0.1s linear"
          }} />

        </div>

      )}
      
      
{/* EDITOR CLÍNICO */}
{editorImg && (
  <div className="vd-photo-editor-overlay">
    <div className="vd-photo-editor">

      {/* ENCABEZADO */}
      <header className="vd-photo-editor-header">
        <div>
          <h2>
            {slotActivo
              ? `Radiografía - ${slotActivo}`
              : "Fotografía clínica"}
          </h2>

          <p>
            Sesión #{sesionActiva?.numero ?? "—"}
            {" · "}
            {sesionActiva?.fecha ?? ""}
          </p>
        </div>

        <button
          type="button"
          className="vd-editor-close"
          onClick={() => {
            if (!subiendo) {
              setEditorImg(null);
              setPantallaCompleta(false);
            }
          }}
          aria-label="Cerrar editor"
          disabled={subiendo}
        >
          ×
        </button>
      </header>

      {/* CUERPO: VISOR Y PANEL LATERAL */}
      <div className="vd-photo-editor-body">

        {/* VISOR */}
        <section className="vd-photo-viewer">
          <div className="vd-photo-canvas">

            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                background: "#05070b",
              }}
            >
              <ReactCrop
                crop={crop}
                aspect={
                  !modoRecorte
                    ? imagenAspectRatio
                    : proporcionRecorte === "1:1"
                      ? 1
                      : proporcionRecorte === "4:3"
                        ? 4 / 3
                        : proporcionRecorte === "16:9"
                          ? 16 / 9
                          : undefined
                }
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(pixelCrop) => setCroppedAreaPixels(pixelCrop)}
                disabled={!modoRecorte}
                keepSelection
                ruleOfThirds={false}
                style={{ maxWidth: "100%", maxHeight: "100%" }}
              >
                <img
                  ref={imagenRecorteRef}
                  src={editorImg}
                  alt="Fotografía clínica"
                  onLoad={(event) => {
                    const img = event.currentTarget;
                    setImagenAspectRatio(img.naturalWidth / img.naturalHeight);
                  }}
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "65vh",
                    width: "auto",
                    height: "auto",
                    transform: `rotate(${rotation}deg) scaleX(${flipX ? -1 : 1}) scale(${zoom})`,
                    filter: `brightness(${brillo}%) contrast(${contraste}%)`,
                    objectFit: "contain",
                  }}
                />
              </ReactCrop>
            </div>

            <span className="vd-photo-zoom-badge">
              {Math.round(zoom * 100)}%
            </span>

            <button
              type="button"
              className="vd-photo-fullscreen"
              title={
                pantallaCompleta
                  ? "Salir de vista amplia"
                  : "Vista amplia"
              }
              onClick={() =>
                setPantallaCompleta(value => !value)
              }
            >
              {pantallaCompleta ? "↙" : "⛶"}
            </button>
          </div>

          {/* CONTROLES INFERIORES DEL VISOR */}
          {!pantallaCompleta && (
            <div className="vd-photo-toolbar">

              <button
                type="button"
                title="Alejar"
                onClick={() =>
                  setZoom(value => Math.max(1, value - 0.1))
                }
              >
                ⊖
              </button>

              <button
                type="button"
                title="Acercar"
                onClick={() =>
                  setZoom(value => Math.min(5, value + 0.1))
                }
              >
                ⊕
              </button>

              <input
                aria-label="Nivel de zoom"
                type="range"
                min={1}
                max={5}
                step={0.01}
                value={zoom}
                onChange={event =>
                  setZoom(Number(event.target.value))
                }
              />

              <span className="vd-photo-zoom-value">
                {Math.round(zoom * 100)}%
              </span>

              <button
                type="button"
                title="Restablecer rotación"
                onClick={() => setRotation(0)}
              >
                ↻
              </button>

              <button
                type="button"
                className="vd-photo-fit-button"
                onClick={() => {
                  setZoom(1);
                  setCrop({ x: 0, y: 0 });
                }}
              >
                ⛶ <span>Ajustar</span>
              </button>
            </div>
          )}
        </section>

        {/* PANEL DERECHO */}
        {!pantallaCompleta && (
          <aside className="vd-photo-sidebar">

            <section className="vd-photo-side-section">
              <div className="vd-photo-section-heading">
                <h3>Ajustes de visualización</h3>

                <button
                  type="button"
                  onClick={() => {
                    setBrillo(100);
                    setContraste(100);
                    setNitidez(0);
                    setZoom(1);
                    setRotation(0);
                    setFlipX(false);
                    setCrop({ x: 0, y: 0 });
                  }}
                >
                  Restablecer
                </button>
              </div>

              {[
                {
                  label: "Brillo",
                  value: brillo - 100,
                  min: -50,
                  max: 50,
                  change: value => setBrillo(100 + value),
                },
                {
                  label: "Contraste",
                  value: contraste - 100,
                  min: -50,
                  max: 50,
                  change: value => setContraste(100 + value),
                },
                {
                  label: "Nitidez",
                  value: nitidez,
                  min: 0,
                  max: 100,
                  change: value => setNitidez(value),
                },
              ].map(control => (
                <div
                  className="vd-photo-slider-control"
                  key={control.label}
                >
                  <div>
                    <label>{control.label}</label>
                    <span>{control.value}</span>
                  </div>

                  <input
                    type="range"
                    min={control.min}
                    max={control.max}
                    value={control.value}
                    onChange={event =>
                      control.change(Number(event.target.value))
                    }
                  />
                </div>
              ))}
            </section>

            <section className="vd-photo-side-section">
              <h3>Herramientas</h3>

              <div className="vd-photo-tools-grid">
                <button
                  type="button"
                  onClick={() =>
                    setZoom(value => Math.min(5, value + 0.25))
                  }
                >
                  <span>⌕</span>
                  Acercar
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setZoom(value => Math.max(1, value - 0.25))
                  }
                >
                  <span>⊖</span>
                  Alejar
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setZoom(1);
                    setCrop({ x: 0, y: 0 });
                  }}
                >
                  <span>⛶</span>
                  Ajustar
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setRotation(value => value + 90)
                  }
                >
                  <span>↻</span>
                  Rotar 90°
                </button>

                <button
                  type="button"
                  onClick={() => setFlipX(value => !value)}
                >
                  <span>◁▷</span>
                  Espejo
                </button>

                <button
                  type="button"
                  aria-pressed={modoRecorte}
                  onClick={() => {
                    setModoRecorte(valor => {
                      const nuevoModo = !valor;

                      if (nuevoModo) {
                        setCrop({
                          unit: "%",
                          x: 10,
                          y: 10,
                          width: 80,
                          height: 80,
                        });
                        setCroppedAreaPixels(null);
                        setProporcionRecorte("libre");
                        setZoom(1);
                      }

                      return nuevoModo;
                    });
                  }}
                  style={{
                    background: modoRecorte ? "#1d4ed8" : undefined,
                    border: modoRecorte ? "1px solid #60a5fa" : undefined,
                  }}
                >
                  <span>✂</span>
                  {modoRecorte ? "Salir de recorte" : "Recortar"}
                </button>
                
                  {modoRecorte && (
                    <select
                      aria-label="Proporción del recorte"
                      value={proporcionRecorte}
                      onChange={(event) => setProporcionRecorte(event.target.value)}
                      style={{
                        gridColumn: "1 / -1",
                        width: "100%",
                        minHeight: 36,
                        borderRadius: 8,
                        padding: "6px 8px",
                        background: "#1e293b",
                        color: "#f8fafc",
                        border: "1px solid #475569",
                      }}
                    >
                      <option value="libre">Libre</option>
                      <option value="1:1">Cuadrado (1:1)</option>
                      <option value="4:3">Horizontal (4:3)</option>
                      <option value="16:9">Panorámico (16:9)</option>
                    </select>
                  )}

                <button
                  type="button"
                  onClick={() => setPantallaCompleta(true)}
                >
                  <span>⛶</span>
                  Pantalla completa
                </button>
              </div>
            </section>

            <section className="vd-photo-side-section vd-photo-information">
              <h3>Información</h3>

              <div>
                <span>Nombre</span>
                <strong>{slotActivo || "Fotografía clínica"}</strong>
              </div>

              <div>
                <span>Tipo</span>
                <strong>Fotografía clínica</strong>
              </div>

              <div>
                <span>Sesión</span>
                <strong>#{sesionActiva?.numero ?? "—"}</strong>
              </div>

              <div>
                <span>Zoom</span>
                <strong>{Math.round(zoom * 100)}%</strong>
              </div>

              <div>
                <span>Rotación</span>
                <strong>{rotation}°</strong>
              </div>

              <div>
                <span>Formato</span>
                <strong>JPEG</strong>
              </div>
            </section>

            {/* ACCIONES AL PIE DEL PANEL */}
            <div className="vd-photo-sidebar-actions">
              <button
                type="button"
                className="vd-photo-delete-button"
                disabled={subiendo}
                onClick={() => {
                  if (sesionActiva && slotActivo) {
                    setEditorImg(null);
                    eliminarFoto(sesionActiva, slotActivo);
                  }
                }}
              >
                <span>▤</span>
                Eliminar
              </button>

              <button
                type="button"
                className="vd-photo-save-button"
                disabled={subiendo}
                onClick={guardar}
              >
                <span>▣</span>
                {subiendo ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </aside>
        )}
      </div>
    </div>
    </div>
)}

    </div>
  );
}

const botonEditorSecundario = {
  minHeight: 40,
  padding: "9px 13px",
  borderRadius: 9,
  border: "1px solid #475569",
  background: "#334155",
  color: "#e2e8f0",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontSize: 12,
};

function IlustracionDental({ nombre }) {
  const ilustraciones = {
    "Frontal": "/clinicas-fotos/frontal.webp",
    "Perfil": "/clinicas-fotos/perfil.webp",
    "Sonrisa": "/clinicas-fotos/sonrisa.webp",
    "Oclusal superior": "/clinicas-fotos/oclusal-superior.webp",
    "Oclusal inferior": "/clinicas-fotos/oclusal-inferior.webp",
    "Lateral derecha": "/clinicas-fotos/lateral-derecha.webp",
    "Frontal intraoral": "/clinicas-fotos/frontal-intraoral.webp",
    "Lateral izquierda": "/clinicas-fotos/lateral-izquierda.webp",
  };

  return (
    <img
      src={ilustraciones[nombre]}
      alt={`Referencia visual: ${nombre}`}
      draggable={false}
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "contain",
        objectPosition: "center",
        flexShrink: 0,
        userSelect: "none",
        pointerEvents: "none",
      }}
    />
  );
}

function Slot({
  nombre,
  s,
  setSlotActivo,
  setSesionActiva,
  fileInputRef,
  seleccionar,
  eliminarFoto,
  setPreview,
}) {
  const foto = s.fotos?.[nombre];
  const url = foto?.url || foto;

  return (
    <article style={{ display: "flex", flexDirection: "column", minWidth: 0, gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 22 }}>
        <span style={{ fontSize: 12, fontWeight: 750, color: "#354966" }}>{nombre}</span>
        <span
          title={url ? "Fotografía cargada" : "Espacio disponible"}
          style={{ width: 7, height: 7, flexShrink: 0, borderRadius: "50%", background: url ? "#22a06b" : "#cbd5e1" }}
        />
      </div>

      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 3",
          minWidth: 0,
          overflow: "hidden",
          border: url ? "1px solid #d9e3f1" : "1px dashed #bdcbe0",
          borderRadius: 13,
          background: "linear-gradient(145deg, #fbfdff, #f1f6fd)",
          boxSizing: "border-box",
        }}
      >
        {url ? (
          <>
            <button
              type="button"
              onClick={() => setPreview(url)}
              title={`Ampliar fotografía: ${nombre}`}
              aria-label={`Ampliar fotografía ${nombre}`}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", padding: 0, border: "none", background: "transparent", cursor: "zoom-in" }}
            >
              <img src={url} alt={nombre} loading="lazy" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
            </button>

            <button
              type="button"
              title="Editar fotografía"
              aria-label={`Editar fotografía ${nombre}`}
              onClick={e => {
                e.stopPropagation();
                setSlotActivo(nombre);
                setSesionActiva(s);
                seleccionar(url);
              }}
              style={{ position: "absolute", top: 8, left: 8, width: 31, height: 31, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,.45)", borderRadius: 9, background: "rgba(15,31,55,.82)", color: "#fff", cursor: "pointer" }}
            >
              ✎
            </button>

            <button
              type="button"
              title="Eliminar fotografía"
              aria-label={`Eliminar fotografía ${nombre}`}
              onClick={e => {
                e.stopPropagation();
                eliminarFoto(s, nombre);
              }}
              style={{ position: "absolute", top: 8, right: 8, width: 31, height: 31, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,.45)", borderRadius: 9, background: "rgba(220,38,38,.92)", color: "#fff", cursor: "pointer", fontSize: 18 }}
            >
              ×
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setSlotActivo(nombre);
              setSesionActiva(s);
              fileInputRef.current?.click();
            }}
            aria-label={`Agregar fotografía: ${nombre}`}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 4,
              padding: "0 8px 12px",
              border: "none",
              background: "transparent",
              color: "#6d82a1",
              cursor: "pointer",
            }}
          >            
            <div
              style={{
                position: "absolute",
                top: 6,
                left: 6,
                right: 6,
                bottom: 66,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                opacity: 0.88,
                pointerEvents: "none",
              }}
            >
              <IlustracionDental nombre={nombre} />
            </div>

            <span
              style={{
                position: "relative",
                zIndex: 1,
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                borderRadius: 13,
                background: "#e7effd",
                color: "#2458b3",
                fontSize: 27,
                boxShadow: "0 3px 10px rgba(36,88,179,.08)",
              }}
            >
              +
            </span>
            <span style={{ position: "relative", zIndex: 1, fontSize: 11, fontWeight: 750, color: "#354966" }}>
              Agregar fotografía
            </span>
            <span style={{ position: "relative", zIndex: 1, fontSize: 10, color: "#8a9bb3" }}>
              {nombre}
            </span>
          </button>
        )}
      </div>
    </article>
  );
}


const estilosEditorClinico = `
.vd-photo-editor-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(2, 5, 10, .88);
  backdrop-filter: blur(9px);
  color: #f8fafc;
  font-family: Inter, system-ui, sans-serif;
}

.vd-photo-editor {
  width: 100%;
  max-width: 1700px;
  height: min(94vh, 1100px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #24354b;
  border-radius: 18px;
  background: linear-gradient(135deg, #142237, #0e1a2b);
  box-shadow: 0 24px 80px rgba(0, 0, 0, .45);
}

.vd-photo-editor-header {
  min-height: 94px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 26px;
  border-bottom: 1px solid #2a3b50;
  box-sizing: border-box;
}

.vd-photo-editor-header h2 {
  margin: 0;
  font-size: 19px;
  line-height: 1.35;
  font-weight: 750;
}

.vd-photo-editor-header p {
  margin: 6px 0 0;
  color: #c0cede;
  font-size: 13px;
}

.vd-editor-close {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border: 1px solid #3a4d65;
  border-radius: 10px;
  background: #17283d;
  color: #fff;
  font-size: 26px;
  cursor: pointer;
}

.vd-photo-editor-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 365px;
  gap: 0;
  padding: 20px 24px 24px;
  overflow: hidden;
}

.vd-photo-viewer {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-right: 20px;
  overflow: hidden;
}

.vd-photo-canvas {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 11px;
  background: #030507;
}

.vd-photo-zoom-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  padding: 8px 12px;
  border: 1px solid #33465e;
  border-radius: 9px;
  background: rgba(16, 29, 46, .88);
  color: #e2e8f0;
  font-size: 12px;
  pointer-events: none;
}

.vd-photo-fullscreen {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: rgba(20, 37, 56, .92);
  color: white;
  font-size: 22px;
  cursor: pointer;
}

.vd-photo-toolbar {
  min-height: 58px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  padding: 0 8px;
  border-radius: 11px;
  background: #1c2b40;
  box-sizing: border-box;
}

.vd-photo-toolbar button {
  min-width: 38px;
  height: 38px;
  border: 0;
  border-radius: 8px;
  background: #263850;
  color: #f8fafc;
  font-size: 19px;
  cursor: pointer;
}

.vd-photo-toolbar input {
  width: auto;
  min-width: 50px;
  max-width: 220px;
  flex: 1;
  accent-color: #1683ff;
}

.vd-photo-zoom-value {
  min-width: 42px;
  color: #e2e8f0;
  font-size: 12px;
  text-align: center;
}

.vd-photo-toolbar .vd-photo-fit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid #3b506b;
  font-size: 13px;
  white-space: nowrap;
}

.vd-photo-sidebar {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-left: 22px;
  border-left: 1px solid #2b3d52;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #40536b transparent;
}

.vd-photo-side-section {
  flex-shrink: 0;
  padding: 0 0 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #2a3b50;
}

.vd-photo-side-section h3 {
  margin: 0 0 17px;
  font-size: 14px;
  font-weight: 700;
}

.vd-photo-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 19px;
}

.vd-photo-section-heading h3 {
  margin: 0;
}

.vd-photo-section-heading button {
  padding: 9px 11px;
  border: 1px solid #3a4c63;
  border-radius: 9px;
  background: #26374d;
  color: #d8e2ef;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
}

.vd-photo-slider-control {
  margin-bottom: 17px;
}

.vd-photo-slider-control:last-child {
  margin-bottom: 0;
}

.vd-photo-slider-control > div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  color: #dbe5f1;
  font-size: 12px;
}

.vd-photo-slider-control > div span {
  color: #e2e8f0;
}

.vd-photo-slider-control input {
  display: block;
  width: 100%;
  margin: 0;
  accent-color: #1683ff;
}

.vd-photo-tools-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.vd-photo-tools-grid button {
  min-width: 0;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 3px;
  border: 1px solid transparent;
  border-radius: 11px;
  background: #26374d;
  color: #edf4fc;
  font-size: 10px;
  line-height: 1.25;
  text-align: center;
  cursor: pointer;
}

.vd-photo-tools-grid button:hover {
  border-color: #506783;
  background: #30445e;
}

.vd-photo-tools-grid button span {
  font-size: 23px;
  line-height: 1;
}

.vd-photo-information {
  margin-bottom: 0;
}

.vd-photo-information > div {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 11px;
  font-size: 12px;
}

.vd-photo-information > div span {
  color: #b3c0d0;
}

.vd-photo-information > div strong {
  min-width: 0;
  overflow-wrap: anywhere;
  color: #f1f5f9;
  font-weight: 600;
}

.vd-photo-sidebar-actions {
  display: grid;
  grid-template-columns: minmax(0, .9fr) minmax(0, 1.25fr);
  gap: 10px;
  margin-top: auto;
  padding-top: 18px;
  flex-shrink: 0;
}

.vd-photo-sidebar-actions button {
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 650;
  white-space: nowrap;
  cursor: pointer;
}

.vd-photo-delete-button {
  border: 1px solid #ef3340;
  background: rgba(127, 29, 29, .15);
  color: #ff4d58;
}

.vd-photo-save-button {
  border: 1px solid #2563eb;
  background: #2162f5;
  color: #fff;
}

.vd-photo-sidebar-actions button:disabled {
  opacity: .6;
  cursor: wait;
}

@media (max-width: 1050px) {
  .vd-photo-editor-overlay {
    padding: 10px;
  }

  .vd-photo-editor {
    height: 96vh;
  }

  .vd-photo-editor-body {
    grid-template-columns: minmax(0, 1fr) 310px;
    padding: 14px;
  }

  .vd-photo-sidebar {
    padding-left: 14px;
  }

  .vd-photo-tools-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .vd-photo-viewer {
    padding-right: 14px;
  }
}

@media (max-width: 720px) {
  .vd-photo-editor-header {
    min-height: 70px;
    padding: 12px 14px;
  }

  .vd-photo-editor-header h2 {
    font-size: 15px;
  }

  .vd-photo-editor-header p {
    font-size: 11px;
  }

  .vd-photo-editor-body {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(240px, 1fr) auto;
    overflow-y: auto;
  }

  .vd-photo-viewer {
    min-height: 280px;
    padding-right: 0;
  }

  .vd-photo-sidebar {
    max-height: 42vh;
    padding: 18px 0 0;
    border-left: 0;
    overflow-y: auto;
  }

  .vd-photo-toolbar {
    gap: 6px;
    padding: 0 5px;
  }

  .vd-photo-toolbar input {
    min-width: 30px;
  }

  .vd-photo-toolbar .vd-photo-fit-button {
    padding: 0 8px;
  }
}

/* PANEL DERECHO COMPACTO: SIN SCROLL EN ESCRITORIO */
@media (min-width: 721px) {
  .vd-photo-editor-body {
    padding: 12px 18px 16px;
    min-height: 0;
  }

  .vd-photo-sidebar {
    padding-left: 16px;
    overflow-y: hidden;
    gap: 0;
  }

  .vd-photo-side-section {
    padding-bottom: 10px;
    margin-bottom: 10px;
  }

  .vd-photo-side-section h3 {
    margin-bottom: 10px;
    font-size: 13px;
  }

  .vd-photo-section-heading {
    margin-bottom: 10px;
    gap: 6px;
  }

  .vd-photo-section-heading h3 {
    font-size: 12px;
  }

  .vd-photo-section-heading button {
    padding: 6px 8px;
    font-size: 10px;
  }

  .vd-photo-slider-control {
    margin-bottom: 9px;
  }

  .vd-photo-slider-control > div {
    margin-bottom: 4px;
    font-size: 11px;
  }

  .vd-photo-slider-control input {
    height: 14px;
  }

  .vd-photo-tools-grid {
    gap: 7px;
  }

  .vd-photo-tools-grid button {
    min-height: 59px;
    gap: 4px;
    padding: 5px 2px;
    border-radius: 9px;
    font-size: 9px;
  }

  .vd-photo-tools-grid button span {
    font-size: 19px;
  }

  .vd-photo-information > div {
    grid-template-columns: 68px minmax(0, 1fr);
    gap: 6px;
    margin-bottom: 7px;
    font-size: 11px;
  }

  .vd-photo-sidebar-actions {
    gap: 7px;
    padding-top: 10px;
  }

  .vd-photo-sidebar-actions button {
    min-height: 40px;
    gap: 5px;
    padding: 6px;
    border-radius: 9px;
    font-size: 10px;
  }
}

/* PANTALLAS CON MENOS ALTURA */
@media (min-width: 721px) and (max-height: 800px) {
  .vd-photo-editor {
    height: 96vh;
  }

  .vd-photo-editor-header {
    min-height: 68px;
    padding: 10px 20px;
  }

  .vd-photo-editor-body {
    padding-top: 9px;
    padding-bottom: 10px;
  }

  .vd-photo-side-section {
    padding-bottom: 7px;
    margin-bottom: 7px;
  }

  .vd-photo-tools-grid button {
    min-height: 49px;
  }

  .vd-photo-tools-grid button span {
    font-size: 17px;
  }

  .vd-photo-information > div {
    margin-bottom: 5px;
  }

  .vd-photo-sidebar-actions {
    padding-top: 6px;
  }
}
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("vd-photo-editor-styles")
) {
  const style = document.createElement("style");
  style.id = "vd-photo-editor-styles";
  style.textContent = estilosEditorClinico;
  document.head.appendChild(style);
}
